import { motion } from "framer-motion";
import LoginForm from "./login-form";
import type { Route } from "../../+types/layout";
import { useModal } from "@/hooks/modal-hooks";
import { setSsoToken, SSO_APP_ID, ssoApi } from "@/redux/sso/sso-api";
import React from "react";
import VerifyAccountForm from "../verify-account/form";
import { authApi } from "@/redux/auth/auth-api";
import { useToast } from "@/hooks/use-toast";
import { getErrorMessage } from "@/hooks/redux-hooks";

export function meta({}: Route.MetaArgs) {
  return [
    { title: "Nettpay" },
    { name: "description", content: "Nettpay | Login" },
  ];
}

const Login = () => {
  const { toast } = useToast();
  const otpVerificationPage = useModal();
  const [submittedData, setSubmittedData] =
    React.useState<SSOLoginPayload | null>(null);

  const [
    login,
    {
      isLoading: ssoLoading,
      isSuccess: ssoLoginSuccessful,
      data: loginResponse,
      isError,
      error,
    },
  ] = ssoApi.useLoginMutation();
  const [
    mainAppLogin,
    { isLoading: mainAppLoading, error: mainAppError, isError: isMainAppError },
  ] = authApi.useLoginMutation();

  const onSubmit = async (data: SSOLoginPayload) => {
    await login(data).unwrap();
    setSubmittedData(data);
  };

  React.useEffect(() => {
    if (ssoLoginSuccessful && loginResponse && loginResponse.sessionId) {
      if (loginResponse.token) setSsoToken(loginResponse.token);
      mainAppLogin({ sessionId: loginResponse.sessionId, appId: SSO_APP_ID });
    }
  }, [ssoLoginSuccessful, loginResponse]);

  React.useEffect(() => {
    if (isMainAppError && mainAppError) {
      toast({
        variant: "destructive",
        title: "Authnetication Failed",
        description: getErrorMessage(mainAppError),
      });
    }
  }, [isMainAppError, mainAppError]);

  React.useEffect(() => {
    if (ssoLoginSuccessful && loginResponse && loginResponse.mfa_required) {
      if (
        loginResponse.mfa_type === "sms_otp" ||
        loginResponse.mfa_type === "email_otp"
      ) {
        otpVerificationPage.toggle(true);
      }
    }
  }, [ssoLoginSuccessful, loginResponse]);

  React.useEffect(() => {
    if (!otpVerificationPage.open) setSubmittedData(null);
  }, [otpVerificationPage.open]);

  const isLoading = ssoLoading || mainAppLoading;

  return (
    <motion.div
      className="flex-1 p-8 md:p-12 bg-white backdrop-blur-lg h-fit rounded-xl"
      style={{ boxShadow: "0px 4px 10px 0px #3182CE" }}
      initial={{ opacity: 0, x: 200 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.8 }}
    >
      <h2 className="text-2xl font-bold mb-12 text-gray-800">
        {otpVerificationPage.open && ssoLoginSuccessful ? (
          "Verify Account With OTP"
        ) : (
          <span>
            Welcome Back. <br /> Sign in to your account.
          </span>
        )}
      </h2>
      {otpVerificationPage.open && submittedData ? (
        <VerifyAccountForm
          ctaText="Login"
          nextPage="/overview"
          verificationType="login"
          username={submittedData.username}
          password={submittedData.password}
          toggleVisiblity={() => otpVerificationPage.toggle(false)}
        />
      ) : (
        <LoginForm
          isLoading={isLoading}
          isError={isError}
          error={error}
          handleLogin={onSubmit}
        />
      )}
    </motion.div>
  );
};

export default Login;
